import { BodyText, Callout } from "@/components/ui";
import { useParams } from "@tanstack/react-router";

import {
  type AdherenceStatus,
  usePatientMedications,
} from "../../network/useMedications";
import { EntrySkeleton } from "../EntrySkeleton";
import { MedicationEntry } from "./MedicationEntry";

interface MedicationsSectionProps {
  adherenceStatus: AdherenceStatus;
  isBatchConfirmingMedications: boolean;
}

export const MedicationsSection = ({
  adherenceStatus,
  isBatchConfirmingMedications,
}: MedicationsSectionProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const {
    data: medications,
    isLoading: isMedicationsLoading,
    isError: isMedicationsError,
  } = usePatientMedications({ patientId, adherenceStatus });

  const isMedicationsEmpty =
    !isMedicationsLoading &&
    !isMedicationsError &&
    (!medications || !medications.length);

  if (isMedicationsEmpty) {
    return (
      <BodyText textColor="tertiary" size="sm">
        {`No ${adherenceStatus} medications documented or prescribed`}
      </BodyText>
    );
  }

  return (
    <div className="w-full h-full flex flex-col gap-2">
      {(isMedicationsLoading || !patientId) && <EntrySkeleton />}
      {isMedicationsError && (
        <div className="flex relative flex-col gap-2 px-3 py-2 w-full">
          <Callout
            className="w-full"
            variant="danger"
            title="Something went wrong!"
            description="Please refresh and try again."
          />
        </div>
      )}
      {!!patientId &&
        medications?.map((medication) => {
          const medicationId = medication.medicationStatement.id as string;
          return (
            <div key={medicationId}>
              <MedicationEntry
                medication={medication}
                adherenceStatus={adherenceStatus}
                isBatchConfirmingMedications={isBatchConfirmingMedications}
              />
            </div>
          );
        })}
    </div>
  );
};
